/**
 * Adjust the closing / settlement dates directly on the loan —
 * `PUT /closing/schedule/{dealId}` (closing-readiness.md §4).
 *
 * A React Final Form (`RffForm` + `Rff*` fields, label above the control,
 * two columns). The pipeline decides the writable set; anything else is a 400
 * with `allowed[]`, which we surface verbatim. `closingScheduledDate` and
 * `wireSentDate` keep their time (the signing time drives the wire cutoff) and
 * go up as the UTC instant the closer picked, like every other datetime the
 * app stores; every other date stores at midnight, so we send date-only
 * (YYYY-MM-DD) and never shift the calendar day.
 *
 * Every control matches the one the loan file uses for the same field:
 * `closingStatus` / `wireStatus` are dropdowns. The backend model carries no
 * option list for either (`closingStatus` is a plain string, `wireStatus`
 * says "selection from predefined options" without listing any), so they read
 * the model schema first and fall back to the drilldown's own lists — the
 * same ones Loan Info offers. The closer is the Closers roster the Summary
 * tab's Assigned team uses, not every user.
 *
 * `ScheduleFieldDialog` edits ONE entry in a modal (a Scheduling entries row).
 *
 * Not for Closed / Settled — those stay on the Mark Closed / Mark Settled modals.
 */
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";

import { closingReadinessApi } from "../../api/closingReadinessApi";
import { normalizeIsoStringToUtc } from "../../_src_shared/utils/functions/formatDateTime";
import { selectOptionsClosingStatus } from "../../constants/selectOptions/selectOptionsClosingStatus";
import {
  LOAN_ROLE_OPTIONS,
  emailInRoles,
} from "../../constants/selectOptions/selectOptionsLoanRoles";
import { selectOptionsWireStatus } from "../../constants/selectOptions/selectOptionsWireStatus";
import { useModelFieldOptions } from "../../hooks/useModelFieldOptions";
import { useOrgGroupOptions } from "../../hooks/useOrgGroupOptions";
import { slate } from "../../theme/colors";
import RffForm from "../finalForm/RffForm";
import RffGroup from "../finalForm/shared/RffGroup";
import RffBooleanToggleField from "../finalForm/inputs/RffBooleanToggleField";
import RffDateField from "../finalForm/inputs/RffDateField";
import RffDateTimeField from "../finalForm/inputs/RffDateTimeField";
import RffDollarField from "../finalForm/inputs/RffDollarField";
import RffSelectField from "../finalForm/inputs/RffSelectField";
import RffTextField from "../finalForm/inputs/RffTextField";
import QuickEditDialog from "../forms/QuickEditDialog";
import { Muted } from "./ReadinessBits";
import { isTrade } from "./closingReadinessUtils";

// Copied VERBATIM from `trade_status_options` in
// ../eastview-os/app/models/loan_acquisitions.py. There is no
// /model_schemas object type for the acquisitions model, so this is the only
// source — keep it in lockstep with the backend enum.
const TRADE_STATUS_OPTIONS = [
  "Indicative",
  "Bid",
  "Committed",
  "Approved",
  "Funded",
  "Settled",
  "Kicked",
  "Declined",
];

// Writable body per pipeline (the doc's table). `kind` picks the control, the
// same one Loan Info / Summary render for that field:
//   date · datetime · dollar · bool (Yes / No) · select (static options) ·
//   schemaSelect (model-schema options, else the drilldown's `options`) ·
//   closer (the Closers roster)
const PRECLOSE_FIELDS = [
  { name: "closingScheduledDate", label: "Scheduled closing (date & time)", kind: "datetime", full: true, hint: "Also marks the loan scheduled and sets Expected closing to this day." },
  { name: "expectedClosingDate", label: "Expected closing", kind: "date" },
  { name: "closingCutoffDate", label: "Closing cutoff", kind: "date" },
  { name: "closingStatus", label: "Closing status", kind: "schemaSelect", options: selectOptionsClosingStatus },
  { name: "loanCloser", label: "Closer", kind: "closer" },
  { name: "wireStatus", label: "Wire status", kind: "schemaSelect", options: selectOptionsWireStatus },
  { name: "wireSentDate", label: "Wire sent (date & time)", kind: "datetime" },
];

const ACQUISITION_FIELDS = [
  { name: "expectedSettlementDate", label: "Expected settlement date", kind: "date" },
  { name: "settlementDate", label: "Settlement date", kind: "date" },
  { name: "expectedClosingDate", label: "Expected closing (locked pre-close)", kind: "date" },
  { name: "tradeStatus", label: "Trade status", kind: "select", options: TRADE_STATUS_OPTIONS },
  { name: "tradeDate", label: "Trade date", kind: "date" },
  { name: "commitmentDate", label: "Commitment date", kind: "date" },
  { name: "wireReleaseDate", label: "Wire release date", kind: "date" },
  { name: "fundingWireAmount", label: "Funding wire amount", kind: "dollar" },
  { name: "loanCloser", label: "Closer", kind: "closer" },
];

// Writable through the same PUT but only offered when ONE entry is edited from
// its Scheduling entries row — the full form drives it through the date
// instead (setting `closingScheduledDate` flips it on).
const PRECLOSE_ROW_ONLY_FIELDS = [
  {
    name: "closingScheduled",
    label: "Closing scheduled",
    kind: "bool",
    hint: "Setting this to No clears the scheduled date.",
  },
];

const fieldDefsFor = (trade) =>
  trade ? ACQUISITION_FIELDS : [...PRECLOSE_FIELDS, ...PRECLOSE_ROW_ONLY_FIELDS];

/**
 * The scheduling entries a user may write through `PUT /closing/schedule` for
 * this loan's pipeline — mirrors `SCHEDULE_WRITABLE_FIELDS` in
 * eastview-os/app/models/closing_readiness.py.
 */
export const scheduleWritableFields = (readiness) =>
  new Set(fieldDefsFor(isTrade(readiness)).map((f) => f.name));

/**
 * The label this app puts on a scheduling field's control. The quick-edit
 * modal HIDES that label and makes the title carry it, so the title has to be
 * this string and not the readiness row's wording — the two differ where the
 * control says more ("Scheduled closing (date & time)" vs "Scheduled closing
 * date"). Falls back to the row's label for a field the FE doesn't define.
 */
export const scheduleFieldLabel = (field, readiness, fallback) =>
  fieldDefsFor(isTrade(readiness)).find((f) => f.name === field)?.label ||
  fallback ||
  field;

// Stored value → the control's value.
//   date-only: the calendar day is authoritative — keep YYYY-MM-DD (DateInput
//              reads it as a UTC-midnight instant and shows that calendar day).
//   datetime:  a UTC instant (the app stores clock times as UTC, see
//              formatDateTime); DateTimeInput shows it in the browser's clock.
//   dollar:    a number.
const toInput = (v, kind) => {
  if (kind === "bool") return typeof v === "boolean" ? v : null;
  if (v == null || v === "") return kind === "dollar" ? null : "";
  const s = String(v);
  if (kind === "date") return s.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || "";
  if (kind === "datetime") {
    const d = new Date(normalizeIsoStringToUtc(s));
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
  }
  if (kind === "dollar") {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return s;
};

// Control value → what we PUT. A datetime goes up as the UTC instant
// (DateTimeInput already emits one); a date goes up as YYYY-MM-DD — DateInput
// emits a UTC-midnight ISO whose first ten characters are the picked day.
const toBody = (v, kind) => {
  if (kind === "bool") return typeof v === "boolean" ? v : null;
  if (v === "" || v == null) return null;
  if (kind === "date") return String(v).match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || null;
  if (kind === "datetime") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (kind === "dollar") {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return v;
};

// Same value, either way of writing "empty".
const same = (a, b) => (a ?? "") === (b ?? "");

// A value already on the loan that a list doesn't carry stays selectable, so
// opening the editor never silently blanks it.
const withCurrent = (options, current) => {
  if (current == null || current === "") return options;
  const has = options.some((o) => (typeof o === "object" ? o?.value : o) === current);
  if (has) return options;
  return typeof options[0] === "object" ? [...options, { label: current, value: current }] : [...options, current];
};

// The Closers roster exactly as the Summary tab's Assigned team builds it
// (AssignedTeamRows): the live "Closers" org group, else the hardcoded
// roster, plus an admin's own email so they can assign themselves.
const useCloserOptions = () => {
  const { optionsForGroup } = useOrgGroupOptions();
  const userEmail = useSelector((state) => state.user?.emailAddress);
  return useMemo(() => {
    const options = optionsForGroup("Closers", LOAN_ROLE_OPTIONS.closers) || [];
    if (!emailInRoles(userEmail, ["admin"]) || options.some((o) => o?.value === userEmail)) return options;
    const adminEntry = LOAN_ROLE_OPTIONS.admin.find((o) => o?.value === userEmail);
    return [...options, { label: adminEntry?.label || userEmail, value: userEmail }];
  }, [optionsForGroup, userEmail]);
};

// One `Rff*` control per field kind; a field sits in half the row unless it
// asks for the `full` width.
const ScheduleField = ({ f, statusOptions, closerOptions, current, full, suppressGrid }) => {
  const common = {
    name: f.name,
    label: f.label,
    size: full || f.full ? 12 : 6,
    helperText: f.hint,
    suppressGrid,
  };
  switch (f.kind) {
    case "bool":
      return <RffBooleanToggleField {...common} />;
    case "date":
      return <RffDateField {...common} />;
    case "datetime":
      return <RffDateTimeField {...common} />;
    case "dollar":
      return <RffDollarField {...common} />;
    case "closer":
      return <RffSelectField {...common} options={withCurrent(closerOptions, current)} placeholder="None" />;
    case "select":
      return <RffSelectField {...common} options={withCurrent(f.options, current)} placeholder="None" />;
    case "schemaSelect": {
      const schema = statusOptions[f.name] || [];
      const options = schema.length ? schema : f.options || [];
      return <RffSelectField {...common} options={withCurrent(options, current)} placeholder="None" />;
    }
    default:
      return <RffTextField {...common} placeholder="" />;
  }
};

// `onlyField` narrows the form to that one entry — same seed, same
// changed-only PUT. `formId` hands the submit to a footer outside the form
// (ScheduleFieldDialog); `onSavingChange` reports the in-flight state to it.
const ScheduleEditForm = ({ readiness, onSaved, onCancel, onlyField, formId, onSavingChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const trade = isTrade(readiness);
  const fields = useMemo(
    () =>
      onlyField
        ? fieldDefsFor(trade).filter((f) => f.name === onlyField)
        : trade
          ? ACQUISITION_FIELDS
          : PRECLOSE_FIELDS,
    [onlyField, trade],
  );
  const dealId = readiness?.dealId;
  const pipeline = readiness?.pipeline;

  // The model's option lists for the two status fields — empty until the
  // backend adds them, so the drilldown's lists (`f.options`) apply meanwhile.
  const closerOptions = useCloserOptions();
  const closingStatusOptions = useModelFieldOptions("closingStatus", []);
  const wireStatusOptions = useModelFieldOptions("wireStatus", []);
  const statusOptions = useMemo(
    () => ({ closingStatus: closingStatusOptions, wireStatus: wireStatusOptions }),
    [closingStatusOptions, wireStatusOptions],
  );

  // Seed from the scheduling entries (they carry the loan's current value).
  const seed = useMemo(() => {
    const byField = {};
    (readiness?.scheduling?.fields || []).forEach((f) => {
      byField[f.field] = f.value;
    });
    if (readiness?.closer && byField.loanCloser == null) byField.loanCloser = readiness.closer;
    if (readiness?.tradeStatus && byField.tradeStatus == null) byField.tradeStatus = readiness.tradeStatus;
    const s = readiness?.scheduling || {};
    if (byField.closingScheduled == null && s.scheduledDate) byField.closingScheduled = true;
    if (byField.expectedClosingDate == null && s.expectedDate) byField.expectedClosingDate = s.expectedDate;
    if (!trade && byField.closingScheduledDate == null && s.scheduledDate) byField.closingScheduledDate = s.scheduledDate;
    if (trade && byField.settlementDate == null && s.scheduledDate) byField.settlementDate = s.scheduledDate;
    if (byField.closingCutoffDate == null && s.cutoffDate) byField.closingCutoffDate = s.cutoffDate;
    const out = {};
    fields.forEach((f) => {
      out[f.name] = toInput(byField[f.name], f.kind);
    });
    return out;
  }, [readiness, fields, trade]);

  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (typeof onSavingChange === "function") onSavingChange(saving);
  }, [saving, onSavingChange]);

  // Only send what changed — the backend treats every key as a write.
  const save = async (values) => {
    if (!dealId) return;
    const changed = fields.filter((f) => !same(toBody(values[f.name], f.kind), toBody(seed[f.name], f.kind)));
    if (!changed.length) {
      enqueueSnackbar("Nothing changed.", { variant: "info" });
      return;
    }
    const body = {};
    changed.forEach((f) => {
      body[f.name] = toBody(values[f.name], f.kind);
    });
    setSaving(true);
    try {
      const res = await closingReadinessApi.updateSchedule(dealId, body, { pipeline });
      const n = Object.keys(res?.updated || body).length;
      enqueueSnackbar(
        `${trade ? "Trade settlement" : "Closing"} schedule updated (${n} field${n === 1 ? "" : "s"}).`,
        { variant: "success" },
      );
      if (typeof onSaved === "function") onSaved(res);
    } catch (err) {
      const data = err?.response?.data;
      const allowed = Array.isArray(data?.allowed) ? ` Allowed: ${data.allowed.join(", ")}.` : "";
      const detail =
        (typeof data?.detail === "string" ? data.detail : data?.detail?.message) ||
        data?.message ||
        "Could not update the schedule.";
      enqueueSnackbar(`${detail}${allowed}`, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <RffForm
      // The multi-field editor lays out on the 2-column grid. The one-field
      // quick edit does NOT: a spaced MUI Grid container is `100% + gutter`
      // wide with matching negative margins, which overflows a 460px modal
      // and grows a scrollbar on both axes. It gets a plain section instead
      // (`RffGroup disableGrid` below) — the root is still a section either
      // way, so the fields sit where a section's fields always sit.
      grid={!onlyField}
      id={formId}
      suppressFooter={!!formId}
      formSpy={false}
      // The form root stretches to its container by default, which a dialog
      // body has no height to give it.
      sx={onlyField ? { minHeight: 0, width: "100%" } : undefined}
      initialValues={seed}
      onSubmit={save}
      onCancel={onCancel}
      loading={saving}
      disabled={saving}
      submitText="Save"
      footerButtonSize="medium"
      footerSx={{ mt: 3.5 }}
      footerStartContent={
        <Muted sx={{ color: slate[400], mr: "auto", alignSelf: "center" }}>
          {trade
            ? "Dates are owned by Capital Markets; closers may edit. Use Mark Settled to settle."
            : "Use Mark Closed to close the loan — this only moves the schedule."}
        </Muted>
      }
    >
      {/* One field: its own section, no grid — every Rff field still sits
          inside a section root, it just stacks instead of laying out. */}
      {onlyField ? (
        <RffGroup disableGrid suppressBottomMargin>
          {fields.map((f) => (
            <ScheduleField
              key={f.name}
              f={f}
              statusOptions={statusOptions}
              closerOptions={closerOptions}
              current={seed[f.name]}
              full
              suppressGrid
            />
          ))}
        </RffGroup>
      ) : (
        fields.map((f) => (
          <ScheduleField
            key={f.name}
            f={f}
            statusOptions={statusOptions}
            closerOptions={closerOptions}
            current={seed[f.name]}
          />
        ))
      )}
    </RffForm>
  );
};

/**
 * One scheduling entry, in the QUICK-EDIT modal (FORM_STYLE_GUIDE.md §12c) —
 * this is a single input, so it gets the 460px shell with the 18/600 title and
 * no header rule, not the 880px form modal. `field` is the entry's name;
 * `label` its readiness label.
 *
 * The field set stays a react-final-form (`ScheduleEditForm` narrowed by
 * `onlyField`), which is why the dialog is handed `formId`: the body brings
 * its own `<form>` and Save submits it by id. That keeps ONE copy of the
 * per-kind controls, the UTC/date-only conversion and the changed-only PUT —
 * the full editor and this modal can't drift apart.
 */
export const ScheduleFieldDialog = ({ open, readiness, field, label, onClose, onSaved }) => {
  const formId = `schedule-field-${useId().replace(/:/g, "")}`;
  const [saving, setSaving] = useState(false);
  const close = useCallback(() => {
    if (!saving) onClose?.();
  }, [saving, onClose]);
  const trade = isTrade(readiness);

  return (
    <QuickEditDialog
      open={open}
      // The control's label is hidden in this shell, so the title is it.
      title={scheduleFieldLabel(field, readiness, label)}
      description={`${trade ? "Trade settlement" : "Closing"} schedule — saves straight to the loan.`}
      onClose={close}
      saving={saving}
      formId={formId}
      // The date / datetime pickers take Enter themselves, so the ⏎ cap would
      // promise something the control eats.
      enterHint={false}
    >
      {open && field && (
        <ScheduleEditForm
          readiness={readiness}
          onlyField={field}
          formId={formId}
          onSavingChange={setSaving}
          onCancel={close}
          onSaved={onSaved}
        />
      )}
    </QuickEditDialog>
  );
};

export default ScheduleEditForm;
